import { Utils } from '@platform/maplibre';
import { Buttons } from '@platform/shared/ui';
import { PeTypes, ProjectTypes } from '@platform/types';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { POINT_COLORS } from '../../../enums/pointColors';
import EmptyCatalog from '../../Datasets/EmptyCatalog';
import useDatasetList from '../../Datasets/useDatasetList';
import SelectableDataset from './SelectableDataset';

interface Props {
  onSelect: (workingLayers: ProjectTypes.UserUploadedWorkingLayer[]) => void;
  onCancel: () => void;
}

const UserUploads: React.FC<Props> = ({ onCancel, onSelect }) => {
  const [selectedDatasets, setSelectedDatasets] = useState<PeTypes.DatasetListItem[]>([]);
  const datasetsTypes = [ProjectTypes.LayerType.POLYGON, ProjectTypes.LayerType.POINT];
  const { isEmptyCatalog, searchControls, noHits, datasets, paging } = useDatasetList(datasetsTypes);

  useEffect(() => {
    // if input datasets changed (after filters were applied or search term...), clear the selection
    setSelectedDatasets([]);
  }, [datasets]);

  const handleToggleDataset = (dataset: PeTypes.DatasetListItem) => {
    const updatedSelection = [...selectedDatasets];
    const layerIndex = selectedDatasets.findIndex((layer) => layer.id === dataset.id);

    if (layerIndex > -1 && selectedDatasets.length > 0) {
      updatedSelection.splice(layerIndex, 1);
    } else {
      updatedSelection.push(dataset);
    }
    setSelectedDatasets(updatedSelection);
  };

  const handleAddLayers = () => {
    const workingLayers: ProjectTypes.UserUploadedWorkingLayer[] = selectedDatasets
      .filter((dataset) => dataset.geoLayerId)
      .map((dataset, idx) => ({
        name: dataset.name,
        id: uuidv4(),
        source: 'user-upload',
        variableSelection: [],
        featureSelection: [],
        maskingSelection: {
          doMask: false,
          features: [],
        },
        type: dataset.type,
        visible: true,
        rendering: {
          type: 'simple',
          style: {
            baseColor: POINT_COLORS[idx % POINT_COLORS.length],
            opacities: Utils.DEFAULT_OPACITIES,
          },
        },
        filter: undefined,
        tooltip: { enabled: false, content: [], action: 'click', title: undefined },
        metadata: {
          datasetId: dataset.id,
          tiles: `${process.env.NX_USER_TILES_SERVER}?x={x}&y={y}&z={z}&layers={layers}&columns={columns}&projection=EPSG-3857`,
          geoLayerId: dataset.geoLayerId ?? '',
          dataLayerId: '0',
          selection: [],
          identifiers: {
            row: {
              variableId: 'ID', // every uploaded dataset has this variableID
              datasetId: '0', // every uploaded dataset has this datasetId
            },
          },
        },
      }));

    onSelect(workingLayers);
  };

  return (
    <>
      <div className="w-full border-t py-4 px-6">{searchControls}</div>
      <div className="flex h-[400px] flex-col overflow-y-auto bg-gray-50">
        {isEmptyCatalog && (
          <div className="flex h-full w-full items-center justify-center py-2">
            <EmptyCatalog />
          </div>
        )}
        <div className="flex min-h-0 w-full flex-col">
          {noHits}
          {datasets.map((dataset) => (
            <SelectableDataset
              key={dataset.id}
              dataset={dataset}
              active={selectedDatasets.includes(dataset)}
              onClick={handleToggleDataset}
            />
          ))}
          {paging}
        </div>
      </div>
      <div className="flex w-full items-center justify-end gap-2 border-t px-6 pt-3">
        <Buttons.Secondary onClick={onCancel}>Cancel</Buttons.Secondary>
        <Buttons.Primary onClick={handleAddLayers} disabled={!selectedDatasets.length} className="w-28 px-6">
          Add
        </Buttons.Primary>
      </div>
    </>
  );
};

export default UserUploads;
